import React, { useMemo } from 'react';
import { extend } from '@thd-nucleus/data-sources';
import {
  PodRow,
  PodSection,
  PodFooter,
  PodSpacer,
  ProductPod,
  ProductImage,
  ProductBadge,
  ProductSponsored,
  ProductRating,
  ProductIdentifier,
  ProductATC,
  PodHeader,
  PodTapper,
} from '@thd-olt-component-react/product-pod';
import proptypes from 'prop-types';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { Price } from '@thd-olt-component-react/price';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import { AddToList } from '@thd-olt-component-react/add-to';
import { Heart, Add } from '@one-thd/sui-icons';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import { createTheme, IconButton, ThemeProvider, Badge } from '@one-thd/sui-atomic-components';
import { useNodeRef, useRecsCarouselPodTelemetry } from '../hooks';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import classNames from 'classnames';
import { getRankedBestSellerBadge } from '../utils/helpers';

const {
  bool: boolType,
  string: stringType,
  number: numType,
  shape: shapeType,
  arrayOf: arrayOfType
} = proptypes;

export const RecsProductPod = (
  {
    // eslint-disable-next-line react/prop-types
    itemId,

    storeId = '',
    position = '',
    parent = '',
    hideSwatches = true,
    hideFavorites = true,
    hideBadge = false,
    hideATC = false,
    hideRating = false,
    scheme = 'drecs',
    anchorProduct = '',
    preferredPriceFlag = false,
    brandTitleMaxLine = 2,
    strategy = 'project-based',
    zoneName = '',
    zonePosition = '',
    podResults = 0,
    noATCFulfillment = true,
    hidePrice = false,
    url,

    analyticImpressionData = {
      name: '',
      component: '',
      id: '',
      type: ''
    },

    swatchImpressionData = {
      position: '',
      recType: '',
      category: '',
      categoryPosition: '',
      name: ''
    },

    theme = null,
    isMobile = false,
    hideSponsoredText = false,
    enableRoundedProductImages = false,
    enableTopSellingRanges = false,
    nValue = '',
    product,
    telemetryKey = ''
  }
) => {
  const badgeContent = enableTopSellingRanges ? getRankedBestSellerBadge(position, scheme) : null;

  const getThemeProps = useTheme(RecsProductPod);
  const { hideAddToList } = getThemeProps.props;
  const anchorId = (anchorProduct.length > 2) ? anchorProduct : 'n/a';
  const merchRec = useMemo(() => {
    return `MERCH=REC-_-${scheme}-_-${anchorId}-_-${position}-_-n/a-_-n/a-_-n/a-_-n/a-_-n/a`;
  }, [scheme, anchorId, position]);

  // nodes
  const [productPodNodeRef, productPodNode] = useNodeRef();

  // Telemetry
  // -----------------
  const {
    handleClick,
    handleClickOnProductImage,
    handleClickOnProductHeader,
    handleClickOnProductRating,
    handleClickOnProductAtcButton
  } = useRecsCarouselPodTelemetry({
    productPodNode,
    parent,
    title: telemetryKey,
    itemId,
    storeId,
    product,
    position,
    url
  });

  const productRatingTheme = createTheme({
    palette: {
      mode: 'light',
      text: {
        primary: 'inverse',
        subtle: '191 191 191'
      }
    }
  });

  const { isB2BCustomer } = useThdCustomer() || {};

  const PodDetails = ({ podItemId }) => {
    const Details = () => {
      return (
        <>
          <PodTapper fitWidth zIndex>
            <ProductHeader
              brand="inline"
              brandFontSize="sm"
              itemId={podItemId}
              brandTitleMaxLine={isMobile ? 3 : brandTitleMaxLine}
              disableShopThisCollection
              merchQueryParam={merchRec}
              productNameLineHeight="tight"
              onClick={handleClickOnProductHeader}
            />
          </PodTapper>
          {theme === 'EndcapBFTheme' && !hideRating ? (
            <ThemeProvider ssr theme={productRatingTheme}>
              <PodTapper fitWidth zIndex>
                <ProductRating
                  itemId={podItemId}
                  hideRating={hideRating}
                  merchQueryParam={merchRec}
                  onlyReviewCount
                  onClick={handleClickOnProductRating}
                />
              </PodTapper>
            </ThemeProvider>
          )
            : !hideRating && (
              <PodTapper fitWidth zIndex>
                <ProductRating
                  itemId={podItemId}
                  hideRating={hideRating}
                  merchQueryParam={merchRec}
                  onlyReviewCount
                  onClick={handleClickOnProductRating}
                />
              </PodTapper>
            )}
          {!hidePrice && (
            <PodTapper zIndex pointerNone aPointer buttonPointer spanPointer>
              <PodSpacer>
                <Price
                  itemId={podItemId}
                  large={false}
                  storeId={storeId}
                  displayEachUom={false}
                  hideSavingsText={!preferredPriceFlag && !zoneName}
                  showPreferredPricingBadge
                  stackWasPrice
                />
              </PodSpacer>
            </PodTapper>
          )}
        </>
      );
    };

    if (zoneName) {
      return <Details />;
    }

    return (
      <PodSection columnAlign noPadding>
        <Details />
      </PodSection>
    );
  };

  PodDetails.propTypes = {
    podItemId: stringType.isRequired
  };

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      data-testid="recs-product-pod"
      className={classNames({
        'sui-bg-primary sui-text-primary sui-p-2': theme !== null,
        '!sui-rounded-[12px] sui-overflow-hidden': theme && enableRoundedProductImages
      })}
    >
      <ProductPod
        itemId={itemId}
        storeId={storeId}
        padding="sui-p-0"
        gap="sui-gap-0"
        analyticImpressionData={analyticImpressionData}
        swatchImpressionData={swatchImpressionData}
        hasSSKU={!hideSwatches}
        analyticsData={{
          parent, position, scheme, anchorProduct, strategy, zoneName, zonePosition, podResults, nValue
        }}
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-1"
              href={url}
              aria-label="Link"
              onClick={handleClick}
            />
            <PodSpacer>
              <PodHeader noPadding>
                {!hideBadge
                  && !hideFavorites && (
                  <PodRow noPadding>
                    {badgeContent ? <Badge variant="strong">{badgeContent}</Badge> : (
                      <ProductBadge
                        itemId={pod.itemId}
                        storeId={storeId}
                      />
                    )}
                  </PodRow>
                )}
                {!hideFavorites
                  && !hideAddToList && (
                  <PodTapper fitWidth zIndex>
                    <PodRow rowReverse noPadding>
                      <AddToList
                        storeId={storeId}
                        products={[{
                          itemId,
                          quantity: pod.quantity ?? 1,
                          price: pod.price ?? 0
                        }]}
                        renderButton={({ onClick }) => (
                          <IconButton
                            aria-label="Add To List"
                            size="large"
                            icon={isB2BCustomer ? Add : Heart}
                            onClick={onClick}
                          />
                        )}
                      />
                    </PodRow>
                  </PodTapper>
                )}
                {!hideBadge
                  && (hideFavorites || hideAddToList) && (
                  <PodSpacer minHeight="40px">
                    <PodRow noPadding>
                      {badgeContent ? <Badge variant="strong">{badgeContent}</Badge> : (
                        <ProductBadge
                          itemId={pod.itemId}
                          storeId={storeId}
                        />
                      )}
                    </PodRow>
                  </PodSpacer>
                )}
              </PodHeader>
            </PodSpacer>
            <PodTapper zIndex>
              <ProductImage
                itemId={pod.itemId}
                showSecondaryImage={pod.showSecondaryImage}
                merchQueryParam={merchRec}
                enableRoundedProductImages={enableRoundedProductImages}
                onClick={handleClickOnProductImage}
              />
            </PodTapper>
            {!hideSwatches
              && (
                <PodTapper zIndex>
                  <SuperSkuMiniSwatches
                    itemId={pod.itemId}
                    storeId={storeId}
                    onChange={pod.onChange}
                    onHover={pod.onSSKUHover}
                    componentImpressionData={{
                      name: 'ProductPod',
                      component: 'ProductPod',
                      position: position + 1,
                      type: 'product',
                      anchorSku: itemId
                    }}
                    isImpressionsRequired
                  />
                </PodTapper>
              )}
            <PodDetails podItemId={pod.itemId} />
            {!hideATC
              && (
                <PodFooter noPadding>
                  <PodSpacer minHeight="40px">
                    <PodTapper zIndex>
                      <ProductATC
                        itemId={pod.itemId}
                        checkGeneric
                        outline
                        noATCFulfillment={noATCFulfillment}
                        onClick={handleClickOnProductAtcButton}
                      />
                    </PodTapper>
                  </PodSpacer>
                </PodFooter>
              )}
            <PodSpacer minHeight={!hideSponsoredText ? '30px' : undefined}>
              <div className="sui-recs-sponsoredbadge">
                <ProductSponsored itemId={pod.itemId} position={position} noBackground noPadding />
              </div>
            </PodSpacer>
          </>
        )}
      />
    </div>
  );
};

RecsProductPod.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductBadge,
  ProductSponsored,
  ProductRating,
  ProductIdentifier,
  ProductATC,
  Price,
  ProductHeader,
  SuperSkuMiniSwatches,
  AddToList
);

RecsProductPod.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  itemId: stringType.isRequired,
  storeId: stringType,
  position: numType,
  parent: stringType,
  hideSwatches: boolType,
  hideFavorites: boolType,
  hideBadge: boolType,
  hideATC: boolType,
  hideRating: boolType,
  scheme: stringType,
  anchorProduct: stringType,
  preferredPriceFlag: boolType,
  brandTitleMaxLine: numType,
  strategy: stringType,
  zoneName: stringType,
  zonePosition: stringType,
  podResults: numType,
  noATCFulfillment: boolType,
  hidePrice: boolType,
  url: stringType.isRequired,
  analyticImpressionData: shapeType({
    name: stringType,
    component: stringType,
    id: stringType,
    type: stringType
  }),
  swatchImpressionData: shapeType({
    position: stringType,
    recType: stringType,
    category: stringType,
    categoryPosition: stringType,
    name: stringType
  }),
  theme: stringType,
  isMobile: boolType,
  hideSponsoredText: boolType,
  enableRoundedProductImages: boolType,
  enableTopSellingRanges: boolType,
  nValue: stringType,
  product: shapeType({
    itemId: stringType.isRequired,
    identifiers: shapeType({
      canonicalUrl: stringType
    }),
    info: shapeType({
      isSponsored: boolType,
      sponsoredBeacon: shapeType({
        onClickBeacons: arrayOfType(stringType),
        onViewBeacons: arrayOfType(stringType)
      }),
      sponsoredMetadata: shapeType({
        trackSource: stringType,
        campaignId: stringType,
        placementId: stringType,
        slotId: stringType,
        sponsoredId: stringType
      })
    })
  }),
  telemetryKey: stringType
};

RecsProductPod.displayName = 'RecsProductPod';

RecsProductPod.themeProps = {
  hideAddToList: boolType
};

RecsProductPod.defaultThemeProps = {
  hideAddToList: false
};
